import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonSpinner } from '@ionic/angular/standalone';
import { AssistantUsageHistory, AssistantUsageHistoryEntry } from '../../models/assistant.models';
import { AssistantService } from '../../services/assistant.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';

@Component({
  selector: 'app-ai-usage-page',
  standalone: true,
  imports: [CommonModule, DatePipe, IonSpinner, InfoTooltipComponent, ApiLoadErrorComponent],
  templateUrl: './ai-usage-page.component.html',
  styleUrls: ['./ai-usage-page.component.scss']
})
export class AiUsagePageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private isRefreshing = false;
  history: AssistantUsageHistory | null = null;
  isLoading = true;
  errorMessage = '';

  constructor(
    private readonly assistantService: AssistantService,
    private readonly foregroundRefresh: ForegroundRefreshService,
    private readonly ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadUsage();
    this.foregroundRefresh.watch(5000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadUsage(true));
  }

  loadUsage(background = false): void {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    if (!background) {
      this.isLoading = true;
      this.errorMessage = '';
    }
    this.assistantService.getUsageHistory().subscribe({
      next: (history) => this.runInAngular(() => {
        this.history = history;
        this.isLoading = false;
        this.errorMessage = '';
        this.isRefreshing = false;
      }),
      error: (error) => this.runInAngular(() => {
        this.isRefreshing = false;
        if (!background) {
          this.isLoading = false;
          this.errorMessage = this.errorText(error);
        }
      })
    });
  }

  tokens(value: number): string {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }

  usd(nanoUsd: number): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(nanoUsd / 1_000_000_000);
  }

  conversationLabel(entry: AssistantUsageHistoryEntry): string {
    return entry.conversationTitle || entry.conversationId;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private errorText(error: unknown): string {
    return typeof error === 'object' && error && 'message' in error && typeof error.message === 'string'
      ? error.message
      : 'AI usage could not be loaded. Try again.';
  }
}
